import {
  ActionIcon,
  Badge,
  Box,
  Button,
  FileButton,
  Flex,
  Menu,
  rem,
  TextInput,
  Tooltip,
} from "@mantine/core";
import { randomId, useDebouncedState, useDisclosure } from "@mantine/hooks";
import {
  IconCaretDown,
  IconFilePlus,
  IconPlus,
  IconRefresh,
  IconSearch,
  IconTrash,
  IconUpload,
  IconX,
} from "@tabler/icons-react";
import {
  MantineReactTable,
  MRT_ColumnDef,
  MRT_RowSelectionState,
  MRT_SortingState,
  useMantineReactTable,
} from "mantine-react-table";
import moment from "moment";
import React, { useCallback, useEffect, useState } from "react";
import * as XLSX from "xlsx";
import { repositoryMdm } from "../../../_base/_const/_constVar";
import { NotificationExtension } from "../../../_base/extension/NotificationExtension";
import { _localization } from "../../../config/location";
import { DemoExportExcelCheckingExit } from "./DemoExportExcelCheckingExit/DemoExportExcelCheckingExit";
import { MessageResponse } from "../../../model/MessageResponse";

type dataSubmit = {
  keySearch: string;
  typeMessage: string;
  startDate: string;
  endDate: string;
  status: string;
};

const formatDateNotTimeZoneee = (
  date: Date | string | null | undefined,
): string => {
  if (date === null || date === undefined) {
    return "";
  }
  return moment(date).startOf("day").format("YYYY-MM-DD[T]HH:mm:ss");
};

// Ô nhập so_ct cho dòng thêm mới.
// Giữ state cục bộ + React.memo => khi gõ KHÔNG rerender lại cả bảng,
// chỉ commit giá trị vào `data` khi blur hoặc nhấn Enter.
const SoCtEditCell = React.memo(
  ({
    rowId,
    defaultValue,
    onCommit,
  }: {
    rowId: string | number;
    defaultValue: string;
    onCommit: (rowId: string | number, value: string) => void;
  }) => {
    const [value, setValue] = useState(defaultValue ?? "");
    return (
      <TextInput
        size="xs"
        autoFocus
        placeholder="Nhập so_ct"
        value={value}
        onChange={(e) => setValue(e.currentTarget.value)}
        onBlur={() => onCommit(rowId, value.trim())}
        onKeyDown={(e) => {
          if (e.key === "Enter") {
            e.currentTarget.blur();
          }
        }}
      />
    );
  },
);

const ImportSyncChecking = () => {
  const headerRef = React.useRef<HTMLDivElement>(null);
  const [data, setData] = useState<any[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  // const [pagination, setPagination] = useState(paginationBase);
  const [pagination, setPagination] = useState({
    pageIndex: 0,
    pageSize: 20,
  });
  const [rowCount, setRowCount] = useState(0);
  const [height, setHeight] = useState(0);
  const [isRefetching, setIsRefetching] = useState(false);
  const [rowSelection, setRowSelection] = useState<MRT_RowSelectionState>({});
  const [visible, { toggle, open, close }] = useDisclosure(false);
  const [sorting, setSorting] = useState<MRT_SortingState>([]);

  const [selectIds, setSelectIds] = useState<string[]>([]); // lưu những dữ liệu đã tích

  const [keySearch, setKeySearch] = useDebouncedState<string>("", 300);

  // Thêm 1 dòng mới (trống) lên đầu bảng để người dùng nhập so_ct
  const handleAddRow = useCallback(() => {
    setData((prev) => [
      { id: `new-${Date.now()}`, so_ct: "", isNew: true },
      ...prev,
    ]);
  }, []);

  // Lưu giá trị so_ct vừa nhập vào đúng dòng (cập nhật theo id)
  const handleCommitSoCt = useCallback(
    (rowId: string | number, value: string) => {
      setData((prev) =>
        prev.map((row) => (row.id === rowId ? { ...row, so_ct: value } : row)),
      );
    },
    [],
  );

  // Xóa 1 dòng theo id
  const handleDeleteRow = useCallback((rowId: string | number) => {
    setData((prev) => prev.filter((row) => String(row.id) !== String(rowId)));
    setRowSelection((prev) => {
      const next = { ...prev };
      delete next[rowId];
      return next;
    });
  }, []);

  // Xóa hàng loạt các dòng đã chọn
  const handleDeleteSelected = useCallback(() => {
    const selectedIds = Object.keys(rowSelection);
    if (selectedIds.length === 0) {
      NotificationExtension.Warn("Chưa chọn dòng nào để xóa");
      return;
    }
    setData((prev) =>
      prev.filter((row) => !selectedIds.includes(String(row.id))),
    );
    setRowSelection({});
  }, [rowSelection]);

  const columns = React.useMemo<MRT_ColumnDef<any>[]>(
    () => [
      {
        header: "STT",
        accessorKey: "STT",
        Cell: ({ row }) => <>{row?.index + 1}</>,
        size: 100,
      },
      {
        accessorKey: "so_ct",
        header: "so_ct",
        minSize: 300,
        Cell: ({ renderedCellValue, row }) =>
          row.original.isNew ? (
            <SoCtEditCell
              rowId={row.original.id}
              defaultValue={row.original.so_ct}
              onCommit={handleCommitSoCt}
            />
          ) : (
            <Badge variant="dot" color="green" size="lg" radius="sm">
              {renderedCellValue}
            </Badge>
          ),
      },
      // {
      //   accessorKey: "ngay_ct",
      //   header: "ngay_ct",
      //   minSize: 300,

      //   Cell: ({ renderedCellValue }: any) => (
      //     <>{formatDateTransfer(renderedCellValue)}</>
      //   ),
      // },
      {
        accessorKey: "action",
        header: "Thao tác",
        // size: 20,
        Cell: ({ row }) => (
          <Box>
            <Flex direction={"row"} align={"center"} gap={"sm"}>
              <Tooltip label="Đồng bộ">
                <ActionIcon
                  variant="light"
                  aria-label="Settings"
                  color="violet"
                  disabled={!String(row.original.so_ct ?? "").trim()}
                  onClick={() => handleDongBoAll(0, [row.original.so_ct])}
                >
                  <IconUpload size={20} stroke={1.5} />
                </ActionIcon>
              </Tooltip>
              <Tooltip label="Xóa">
                <ActionIcon
                  variant="light"
                  aria-label="Delete"
                  color="red"
                  onClick={() => handleDeleteRow(row.original.id)}
                >
                  <IconTrash size={20} stroke={1.5} />
                </ActionIcon>
              </Tooltip>
            </Flex>
          </Box>
        ),
      },
    ],
    [data, rowCount],
  );
  const table = useMantineReactTable({
    columns,
    data: data || [],
    getRowId: (row) => String(row.id),
    enableColumnResizing: true,

    enableTopToolbar: false,
    enableColumnFilters: true,
    enableSorting: true,
    enableColumnActions: true,
    mantineTopToolbarProps: {
      style: {
        borderBottom: "3px solid rgba(128, 128, 128, 0.5)",
        marginBottom: 5,
      },
    },
    localization: _localization,

    initialState: {
      showColumnFilters: false,
      columnPinning: {
        left: ["STT", "so_ct"],
        right: ["action"],
      },
      columnVisibility: { id: true },
      density: "xs",
    },
    // Dòng nào chưa có so_ct (không có data) thì disable ô chọn
    enableRowSelection: true,
    mantineTableContainerProps: {
      style: { maxHeight: height, minHeight: height },
    },
    enableStickyHeader: true,
    onRowSelectionChange: setRowSelection,
    manualFiltering: false,
    manualPagination: true,
    manualSorting: false,
    rowCount,
    onSortingChange: setSorting,
    onPaginationChange: setPagination,
    mantineTableBodyCellProps: ({ row }) => ({
      style: {
        fontWeight: 550,
        fontSize: "12.5px",
        padding: "5px 15px",
      },
    }),
    state: {
      isLoading,
      pagination,
      showProgressBars: isRefetching,
      showSkeletons: isLoading,
      rowSelection,
      sorting,
    },
    mantinePaginationProps: {
      showRowsPerPage: true,
      withEdges: true,
      rowsPerPageOptions: ["20", "50", "100"],
    },
    paginationDisplayMode: "pages",
    enableColumnPinning: true,
    mantineTableProps: {
      striped: false,
    },
  });
  const handleGetListOrder = async () => {
    const res = await repositoryMdm.get<MessageResponse<any>>(
      `/api/v1/RawQuery/execute?SqlQuery=${keySearch}`,
    );

    if (res && res.success) {
      const data = Array.from(
        new Set<string>(
          (res.data ?? []).map((i: string) => String(i ?? "").trim()),
        ),
      ).filter(Boolean);

      const dataSet = data.map((i) => ({
        id: `new-${randomId()}`,
        so_ct: i,
        isNew: true,
      }));

      setData((prev) => {
        const existed = new Set(
          prev.map((row) => String(row.so_ct ?? "").trim()).filter(Boolean),
        );
        return [...prev, ...dataSet.filter((row) => !existed.has(row.so_ct))];
      });
    }
  };

  //Import excel
  const handleFileUpload = (file: File | null) => {
    if (!file) {
      NotificationExtension.Warn("Không có tập tin nào được chọn");
      return;
    }

    const reader = new FileReader();
    reader.onload = (event) => {
      try {
        const binaryStr = event.target?.result;
        const workbook = XLSX.read(binaryStr, { type: "binary" });

        // Lấy sheet đầu tiên
        const sheetName = workbook.SheetNames[0];
        const sheet = workbook.Sheets[sheetName];

        // Chuyển dữ liệu từ sheet thành JSON
        const jsonData = XLSX.utils.sheet_to_json(sheet);

        // Map dữ liệu file Excel, trim() tất cả các trường dữ liệu
        const importedRows = jsonData.map((row: any, index: number) => {
          const trimmedRow = Object.fromEntries(
            Object.entries(row).map(([key, value]) => [
              key,
              typeof value === "string" ? value.trim() : value,
            ]),
          );
          return {
            id: `import-${Date.now()}-${index}`, // id duy nhất, tránh trùng dữ liệu cũ
            ...trimmedRow, // Các cột từ file Excel đã được trim()
            // ngay_ct: formatExcelDate(row.ngay_ct),
          };
        });

        // Thêm vào data hiện có thay vì thay thế
        setData((prev) => [...prev, ...importedRows]);

        NotificationExtension.Success("Import dữ liệu thành công!");
      } catch (err) {
        console.error(err);
        NotificationExtension.Warn("Có lỗi xảy ra khi xử lý file.");
      }
    };

    reader.readAsBinaryString(file);
  };
  // Hàm chuyển đổi số serial của Excel thành ngày có định dạng "DD-MM-YYYY HH:MM:SS"
  const formatExcelDate = (serial: number) => {
    if (!serial || isNaN(serial)) return null; // Nếu không phải số hợp lệ, trả về null

    const dateObj = XLSX.SSF.parse_date_code(serial);
    return `${pad(dateObj.d)}-${pad(dateObj.m)}-${dateObj.y} ${pad(
      dateObj.H,
    )}:${pad(dateObj.M)}:${pad(dateObj.S)}`;
  };

  // Hàm thêm số 0 vào trước nếu nhỏ hơn 10
  const pad = (num: number) => (num < 10 ? `0${num}` : num);
  //Đồng bộ tất cả
  const handleDongBoAll = async (type: number, Codes?: any) => {
    const valueSubmit = {
      codes:
        selectIds &&
        selectIds.filter((i) => i && String(i).length > 0).length > 0
          ? selectIds.filter((i) => i && String(i).length > 0)
          : Codes,
      type: type, //type 0 là tạo mới, 1 là cập nhật,2 là delete
      typeMessage: "",
    };
    if (valueSubmit) {
      try {
        const response = await repositoryMdm.post(
          "/api/v1/SyncData/repeat-sync-checking-exist",
          valueSubmit,
        );
        console.log("response", response);
        if (response && response.success) {
          if (type === 1) {
            NotificationExtension.Success("Cập nhật thành công");
          } else if (type === 0) {
            NotificationExtension.Success("Tạo mới thành công");
          }
        } else {
          NotificationExtension.Fails("Có lỗi xảy ra.Vui lòng thử lại!");
        }
      } catch (error) {
        console.log("error", error);
      }
    }
    setIsLoading(false);
    setIsRefetching(false);
  };
  useEffect(() => {
    // Selection giờ dựa theo id (getRowId), nên key chính là id của dòng
    const selectedIds = Object.keys(rowSelection);
    // Lấy so_ct tương ứng với các id đã chọn
    const so_cts = selectedIds
      .map((id) => data.find((row) => String(row.id) === id)?.so_ct)
      .filter(Boolean);
    // Cập nhật mảng ID đã chọn
    setSelectIds(so_cts);
  }, [rowSelection, data]);
  useEffect(() => {
    const headerHeight = headerRef.current?.offsetHeight || 0;
    const handleResize = () => {
      // 190 là chiều cao của phần phân trang
      // headerHeight là chiều cao của phần header
      setHeight(window.innerHeight - (260 + headerHeight));
    };

    handleResize(); // Set initial height
    window.addEventListener("resize", handleResize); // Update height on window resize

    return () => {
      window.removeEventListener("resize", handleResize); // Clean up event listener
    };
  }, []);
  return (
    <>
      <Flex
        direction={"row"}
        align={"center"}
        justify={"space-between"}
        style={{
          border: "1px solid #dee2e6",
          overflowX: "hidden",
          padding: "10px 10px",
        }}
        gap={"md"}
      >
        <Flex gap={"md"} w={"30%"}>
          <TextInput
            placeholder="Nhập từ khóa để lấy danh sách mã đơn"
            leftSection={<IconSearch size={14} color="#15aabf" />}
            w={300}
            defaultValue={keySearch}
            onChange={(e) => setKeySearch(e.currentTarget.value)}
          />
          <Button
            leftSection={<IconSearch size={14} />}
            onClick={() => {
              handleGetListOrder();
            }}
            disabled={!keySearch || String(keySearch).length === 0}
          >
            Tìm kiếm
          </Button>
        </Flex>
        <Flex
          direction={"row"}
          align={"center"}
          justify={"end"}
          gap={"md"}
          w={"70%"}
        >
          <DemoExportExcelCheckingExit />
          <FileButton
            onChange={(file) => file && handleFileUpload(file)}
            accept=".xlsx, .xls"
          >
            {(props) => (
              <Button
                leftSection={<IconFilePlus size={20} />}
                variant="light"
                {...props}
                style={{
                  cursor: "pointer",
                  display: "inline-block",
                }}
              >
                Import Excel
              </Button>
            )}
          </FileButton>
          <Button
            leftSection={<IconPlus size={20} />}
            variant="light"
            color="teal"
            onClick={handleAddRow}
          >
            Thêm mới
          </Button>
          <Button
            leftSection={<IconTrash size={20} />}
            variant="light"
            color="red"
            disabled={Object.keys(rowSelection).length === 0}
            onClick={handleDeleteSelected}
          >
            Xóa đã chọn
          </Button>
          <Menu shadow="md" width={220}>
            <Menu.Target>
              <Button
                // size="xs"
                rightSection={
                  <IconCaretDown style={{ width: rem(16), height: rem(16) }} />
                }
              >
                Chức năng đồng bộ
              </Button>
            </Menu.Target>

            <Menu.Dropdown>
              <Menu.Label>Application</Menu.Label>
              <Menu.Item
                leftSection={
                  <IconPlus style={{ width: rem(14), height: rem(14) }} />
                }
                onClick={() => handleDongBoAll(0)}
              >
                Tạo lại đơn
              </Menu.Item>
              <Menu.Item
                leftSection={
                  <IconRefresh style={{ width: rem(14), height: rem(14) }} />
                }
                onClick={() => handleDongBoAll(1)}
              >
                Cập nhật đơn
              </Menu.Item>
              <Menu.Item
                leftSection={
                  <IconX style={{ width: rem(14), height: rem(14) }} />
                }
                onClick={() => handleDongBoAll(2)}
              >
                Xóa đơn
              </Menu.Item>
            </Menu.Dropdown>
          </Menu>
        </Flex>
      </Flex>
      <div className="mt-5" style={{ marginTop: "10px" }}>
        <MantineReactTable table={table} />
      </div>
    </>
  );
};

export default ImportSyncChecking;
